/**
 * CortisolProgramCard — 28-дневна кортизолова програма по седмици.
 * Навици с отметка за деня, примерно меню, седмична лента и серия дни.
 * Данни: GET /api/cortisol/program · POST /program/start · /program/checkin
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CalendarCheck, Flame, Lock, Loader2, Utensils, CheckCircle2, Circle, Info,
  BellRing,
} from "lucide-react";
import { useAuth } from "@/App";
import CortisolBreathTimer from "@/components/stress/CortisolBreathTimer";
import CortisolEveningRitualCard from "@/components/stress/CortisolEveningRitualCard";
import CortisolReportCard from "@/components/stress/CortisolReportCard";

const today = () => new Date().toISOString().slice(0, 10);

export default function CortisolProgramCard({ isDark = false }) {
  const { API } = useAuth();
  const [d, setD] = useState(null);
  const [rem, setRem] = useState(null);
  const [showRem, setShowRem] = useState(false);
  const [tab, setTab] = useState(1);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, r] = await Promise.all([
        axios.get(`${API}/cortisol/program`),
        axios.get(`${API}/cortisol/reminder-settings`).catch(() => null),
      ]);
      setD(p.data);
      setTab(p.data.current_week || 1);
      if (r?.data) setRem(r.data);
    } catch { setD(null); }
  }, [API]);

  useEffect(() => { load(); }, [load]);
  if (!d) return null;

  const startProgram = async (nextRound = false) => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/cortisol/program/start${nextRound ? "?next_round=true" : ""}`);
      setD(data);
      setTab(data.current_week || 1);
      toast.success(nextRound
        ? `Кръг ${data.round} започна — целите са по-високи`
        : "Програмата започна — ден 1 от 28");
    } catch { toast.error("Неуспешно"); } finally { setBusy(false); }
  };

  const saveReminders = async (patch) => {
    try {
      const { data } = await axios.post(`${API}/cortisol/reminder-settings`, patch);
      setRem(data);
      toast.success("Напомнянията са обновени");
    } catch { toast.error("Не се записа"); }
  };

  const toggle = async (key, done) => {
    try {
      const { data } = await axios.post(`${API}/cortisol/program/checkin`, {
        date: today(), habit_key: key, done,
      });
      setD(data);
    } catch { toast.error("Отметката не се запази"); }
  };

  const week = d.weeks.find((w) => w.n === tab) || d.weeks[0];
  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const border = isDark ? "border-white/10" : "border-slate-200";

  return (
    <div className={`rounded-2xl border p-4 mb-4 ${card}`} data-testid="cortisol-program-card">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="text-[13px] font-extrabold inline-flex items-center gap-1.5">
            <CalendarCheck className="w-4 h-4 text-orange-500" /> 28-дневна кортизолова програма
          </div>
          <div className="text-[11px] opacity-75 mt-0.5" data-testid="cortisol-program-status">
            {d.enrolled
              ? (d.finished ? "Програмата е завършена — задръж трите навика."
                : d.today_bg)
              : "Четири седмици: Ритъм → Гориво → Устойчивост → Задържане."}
            {d.enrolled && d.round > 1 && (
              <span className="ml-1 font-bold text-orange-500"
                data-testid="cortisol-program-round">· кръг {d.round}</span>
            )}
          </div>
        </div>
        {d.enrolled ? (
          <div className="flex items-center gap-2">
            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1
              ${d.streak > 0 ? "bg-orange-500/20 text-orange-600" : "bg-slate-500/15 opacity-70"}`}
              data-testid="cortisol-program-streak">
              <Flame className="w-3.5 h-3.5" /> {d.streak} дни
            </span>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600"
              data-testid="cortisol-program-progress">
              {d.progress.percent}%
            </span>
          </div>
        ) : (
          <button onClick={() => startProgram(false)} disabled={busy}
            className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white
                       bg-gradient-to-r from-orange-500 to-amber-600 inline-flex items-center gap-1.5
                       disabled:opacity-50"
            data-testid="cortisol-program-start">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Започни програмата
          </button>
        )}
      </div>

      {/* покана за следващия кръг */}
      {d.enrolled && d.finished && (
        <div className="mt-3 p-3 rounded-xl bg-gradient-to-r from-orange-500/10 to-amber-500/10
                        border border-orange-500/30"
          data-testid="cortisol-round-invite">
          <div className="text-[12px] font-extrabold">
            Кръг {d.next_round}: същите четири седмици, по-високи цели
          </div>
          <div className="text-[11px] opacity-80 mt-0.5">
            Светлина 20 минути, кафе до 11:00, лягане преди 22:30, 9-часов прозорец,
            три дихателни сесии, студ до 2 минути, 8 000+ стъпки и измерване на ритъма всяка седмица.
            Напредъкът от кръг {d.round} остава в отчета, серията дни не се нулира.
          </div>
          <button onClick={() => startProgram(true)} disabled={busy}
            className="mt-2 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white
                       bg-gradient-to-r from-orange-500 to-amber-600 inline-flex items-center gap-1.5
                       disabled:opacity-50"
            data-testid="cortisol-round-start">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Започни кръг {d.next_round}
          </button>
        </div>
      )}

      {/* час на напомнянията */}
      {d.enrolled && rem && (
        <div className="mt-3" data-testid="cortisol-reminders">
          <button onClick={() => setShowRem((s) => !s)}
            className="text-[11px] font-bold inline-flex items-center gap-1 opacity-80"
            data-testid="cortisol-reminders-toggle">
            <BellRing className="w-3.5 h-3.5" />
            Напомняния: {rem.enabled ? `${rem.morning} и ${rem.evening}` : "изключени"}
          </button>
          {showRem && (
            <div className={`mt-2 p-3 rounded-xl border ${border} space-y-2`}
              data-testid="cortisol-reminders-panel">
              <label className="flex items-center justify-between gap-2 text-[11px]">
                Напомняния
                <input type="checkbox" checked={!!rem.enabled}
                  onChange={(e) => saveReminders({ enabled: e.target.checked })}
                  className="accent-orange-500" data-testid="cortisol-reminders-enabled" />
              </label>
              <label className="flex items-center justify-between gap-2 text-[11px]">
                Навикът на деня
                <input type="time" value={rem.morning}
                  onChange={(e) => setRem({ ...rem, morning: e.target.value })}
                  onBlur={(e) => saveReminders({ morning: e.target.value })}
                  className={`px-2 py-1 rounded-lg border text-[12px] ${
                    isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`}
                  data-testid="cortisol-reminders-morning" />
              </label>
              <label className="flex items-center justify-between gap-2 text-[11px]">
                Вечерен ритуал
                <input type="time" value={rem.evening}
                  onChange={(e) => setRem({ ...rem, evening: e.target.value })}
                  onBlur={(e) => saveReminders({ evening: e.target.value })}
                  className={`px-2 py-1 rounded-lg border text-[12px] ${
                    isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`}
                  data-testid="cortisol-reminders-evening" />
              </label>
              <div className="flex gap-1.5">
                {[["daily", "всеки ден"], ["weekdays", "без събота и неделя"]].map(([v, label]) => (
                  <button key={v} onClick={() => saveReminders({ days: v })}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-bold border
                      ${rem.days === v ? "border-orange-500 bg-orange-500/15 text-orange-600"
                                       : `${border} opacity-70`}`}
                    data-testid={`cortisol-reminders-days-${v}`}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="text-[9px] opacity-50">
                Часовете са по Вашата часова зона от профила.
              </div>
            </div>
          )}
        </div>
      )}

      {d.adapted && (
        <div className="mt-2 text-[10px] p-2 rounded-lg bg-sky-500/10 text-sky-600 flex items-start gap-1.5"
          data-testid="cortisol-program-adapted">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {d.adapt_note_bg}
        </div>
      )}

      {d.renal_note_bg && (
        <div className="mt-2 text-[10px] p-2 rounded-lg bg-cyan-500/10 text-cyan-600 flex items-start gap-1.5"
          data-testid="cortisol-program-renal-note">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {d.renal_note_bg}
        </div>
      )}

      {/* седмици */}
      <div className="flex flex-wrap gap-1.5 mt-3" data-testid="cortisol-program-weeks">
        {d.weeks.map((w) => (
          <button key={w.n} onClick={() => setTab(w.n)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-bold border inline-flex items-center gap-1
              ${tab === w.n ? "border-orange-500 bg-orange-500/15 text-orange-600" : `${border} opacity-70`}`}
            data-testid={`cortisol-week-tab-${w.n}`}>
            {w.locked && <Lock className="w-3 h-3" />} {w.n}. {w.title_bg}
            {d.enrolled && <span className="opacity-60">· {w.percent}%</span>}
          </button>
        ))}
      </div>

      <div className={`mt-3 p-3 rounded-xl border ${border}`} data-testid={`cortisol-week-${week.n}`}>
        <div className="text-[11px] opacity-85">{week.focus_bg}</div>
        {d.enrolled && (
          <div className="mt-2">
            <div className="h-1.5 rounded-full overflow-hidden bg-slate-500/20">
              <div className="h-full bg-gradient-to-r from-orange-500 to-amber-500"
                style={{ width: `${week.percent}%` }} />
            </div>
            <div className="text-[10px] opacity-60 mt-1">
              {week.done} от {week.slots} отметки · {week.start_date} → {week.end_date}
            </div>
          </div>
        )}

        <div className="mt-3 space-y-1.5" data-testid={`cortisol-week-habits-${week.n}`}>
          {week.habits.map((h) => (
            <div key={h.key} className={`p-2 rounded-lg border ${border} flex items-start gap-2`}
              data-testid={`cortisol-habit-${h.key}`}>
              <button disabled={!d.enrolled || week.locked}
                onClick={() => toggle(h.key, !h.done_today)}
                className="mt-0.5 disabled:opacity-40"
                data-testid={`cortisol-habit-toggle-${h.key}`}>
                {h.done_today
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  : <Circle className="w-4 h-4 opacity-50" />}
              </button>
              <div className="min-w-0">
                <div className={`text-[11px] font-bold ${h.done_today ? "line-through opacity-60" : ""}`}>
                  {h.title_bg}
                  {d.enrolled && h.days_done > 0 && (
                    <span className="ml-1 text-[10px] font-normal opacity-60">
                      · {h.days_done}/7 дни
                    </span>
                  )}
                </div>
                <div className="text-[10px] opacity-70">{h.how_bg}</div>
              </div>
            </div>
          ))}
        </div>

        <div className={`mt-3 p-2.5 rounded-lg border ${border}`} data-testid={`cortisol-week-menu-${week.n}`}>
          <div className="text-[10px] font-bold uppercase opacity-60 inline-flex items-center gap-1">
            <Utensils className="w-3.5 h-3.5" /> Примерен ден от менюто
          </div>
          <div className="text-[11px] mt-1 space-y-0.5">
            <div><b>Закуска:</b> {week.menu.breakfast_bg}</div>
            <div><b>Обяд:</b> {week.menu.lunch_bg}</div>
            <div><b>Вечеря:</b> {week.menu.dinner_bg}</div>
          </div>
          <div className="text-[10px] opacity-60 mt-1">{week.menu.note_bg}</div>
          <a href="/dashboard/diary"
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600"
            data-testid="cortisol-open-plate">
            Отвори Симулатора на чинията — кортизолова проверка
          </a>
        </div>
      </div>

      <div className="mt-3">
        <CortisolBreathTimer isDark={isDark} onSaved={(data) => setD(data)} />
      </div>

      {d.enrolled && (
        <div className="mt-3">
          <CortisolEveningRitualCard evening={d.evening} isDark={isDark}
            onChange={(data) => setD(data)} />
        </div>
      )}

      <CortisolReportCard isDark={isDark} refreshKey={d.progress.done} />
    </div>
  );
}
